/**
 * ReportesTab — manage construction_reports for an investor.
 *
 * Uses:
 *   GET    /construction-reports?investor_user_id=...
 *   POST   /construction-reports
 *   PATCH  /construction-reports/{id}
 *   DELETE /construction-reports/{id}
 *
 * V1: URL-based pdf_url + photo_urls only. No file upload here.
 */
import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Plus, Trash, EyeSlash, Eye } from "@phosphor-icons/react";
import VisibilityScopeSelector, { ScopeChip } from "@/components/admin/operaciones/VisibilityScopeSelector";

const STATUS_OPTIONS = [
  { value: "on_track",  label: "En tiempo" },
  { value: "delayed",   label: "Con retraso" },
  { value: "completed", label: "Completado" },
];

const STATUS_TONE = {
  on_track:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  delayed:   "bg-amber-50 text-amber-800 border-amber-200",
  completed: "bg-stone-100 text-stone-700 border-stone-200",
};

// Sentinel value used by the unified villa picker to represent the
// project-wide alcance (visibility_scope = "project_all"). Kept distinct
// from real villa_ids so it can never collide.
const PROJECT_WIDE_VALUE = "__project_all__";

const EMPTY_FORM = {
  title: "",
  villa_id: "",
  reporting_period: "",
  status: "on_track",
  progress_pct: 0,
  completed_milestone: "",
  next_milestone: "",
  summary: "",
  pdf_url: "",
  photo_urls_text: "",
  scope: "investor",
  confirm_project_all: false,
  // When true, the unified Villa picker is the source of truth and the
  // advanced VisibilityScopeSelector is hidden. Admins can still flip to
  // advanced mode for the rare internal / investor_only scopes.
  use_simple_picker: true,
  // Tracks the villa picker selection: "" / "__project_all__" / <villa_id>.
  picker_value: "",
};

export default function ReportesTab({ investor, villa, villas = [] }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!investor) return;
    setLoading(true);
    try {
      const { data } = await api.get("/construction-reports", {
        params: { investor_user_id: investor.user_id },
      });
      setReports(data || []);
      setError("");
    } catch (e) {
      setError(formatError(e));
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investor?.user_id]);

  const resetForm = () => {
    const preselectedVilla = villa?.villa_id || "";
    setForm({
      ...EMPTY_FORM,
      villa_id: preselectedVilla,
      picker_value: preselectedVilla,
      scope: preselectedVilla ? "villa" : "investor",
    });
    setEditId(null);
  };

  const startEdit = (r) => {
    setEditId(r.report_id);
    // Choose between the simple unified picker (Villa o "Todas las villas")
    // and the advanced scope selector based on the existing row's scope.
    const scope = r.visibility_scope || (r.visible_to_investor && r.villa_id ? "villa" : "investor");
    const isSimple = scope === "project_all" || scope === "villa";
    let pickerValue = "";
    if (scope === "project_all") pickerValue = PROJECT_WIDE_VALUE;
    else if (scope === "villa") pickerValue = r.villa_id || "";
    setForm({
      title: r.title || "",
      villa_id: r.villa_id || "",
      reporting_period: r.reporting_period || "",
      status: r.status || "on_track",
      progress_pct: r.progress_pct ?? 0,
      completed_milestone: r.completed_milestone || "",
      next_milestone: r.next_milestone || "",
      summary: r.summary || "",
      pdf_url: r.pdf_url || "",
      photo_urls_text: (r.photo_urls || []).join("\n"),
      scope,
      // Auto-confirm when restoring an existing project_all row — admin
      // already opted in once; editing other fields shouldn't require
      // re-confirming the broadcast.
      confirm_project_all: scope === "project_all",
      use_simple_picker: isSimple,
      picker_value: pickerValue,
    });
    setShowForm(true);
  };

  // Handler for the unified villa picker.
  //   __project_all__ → scope=project_all + auto-confirm + villa_id cleared
  //   any villa_id    → scope=villa + villa_id set
  //   ""              → keep current scope (form will require a real pick)
  const onPickerChange = (value) => {
    if (value === PROJECT_WIDE_VALUE) {
      setForm((f) => ({
        ...f,
        picker_value: value,
        villa_id: "",
        scope: "project_all",
        confirm_project_all: true,
      }));
    } else if (value) {
      setForm((f) => ({
        ...f,
        picker_value: value,
        villa_id: value,
        scope: "villa",
        confirm_project_all: false,
      }));
    } else {
      setForm((f) => ({ ...f, picker_value: "", villa_id: "" }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!investor) return;
    if (!form.title.trim()) {
      setError("El título es obligatorio.");
      return;
    }
    if (!form.scope) {
      setError("Selecciona un alcance de visibilidad.");
      return;
    }
    if (form.scope === "villa" && !form.villa_id) {
      setError("Para el alcance 'Inversores de esta villa' selecciona una villa.");
      return;
    }
    if (form.scope === "project_all" && !form.confirm_project_all) {
      setError("Marca la confirmación explícita para alcance 'Todas las villas / proyecto'.");
      return;
    }
    const photo_urls = form.photo_urls_text
      .split(/\r?\n/)
      .map((u) => u.trim())
      .filter(Boolean);
    // Resolve scope → investor / villa fields. Backend re-validates.
    const scope = form.scope;
    const scopedInvestor =
      scope === "investor" || scope === "investor_all_villas" ? investor.user_id : null;
    const scopedVilla = scope === "villa" ? (form.villa_id || null) : null;
    const payload = {
      title: form.title.trim(),
      investor_user_id: scopedInvestor,
      villa_id: scopedVilla,
      reporting_period: form.reporting_period.trim() || null,
      status: form.status,
      progress_pct: Number(form.progress_pct) || 0,
      completed_milestone: form.completed_milestone.trim() || null,
      next_milestone: form.next_milestone.trim() || null,
      summary: form.summary.trim() || null,
      pdf_url: form.pdf_url.trim() || null,
      photo_urls,
      visibility_scope: scope,
      confirm_project_all: scope === "project_all" ? !!form.confirm_project_all : undefined,
    };
    setBusy(true);
    setError("");
    try {
      if (editId) {
        await api.patch(`/construction-reports/${editId}`, payload);
      } else {
        await api.post("/construction-reports", payload);
      }
      setShowForm(false);
      resetForm();
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleVisibility = async (r) => {
    // Toggle between "internal" and the most natural visible scope. Legacy
    // rows (no visibility_scope) need to be re-classified explicitly via
    // Edit — surface the rule here instead of silently picking a scope.
    const currentScope = r.visibility_scope;
    if (!currentScope) {
      setError("Este reporte no tiene alcance explícito. Edita el reporte y selecciona un alcance.");
      return;
    }
    let nextScope;
    if (currentScope === "internal") {
      // Pick the most natural visible scope from existing data.
      if (r.investor_user_id) nextScope = "investor";
      else if (r.villa_id)    nextScope = "villa";
      else {
        setError("Edita el reporte y elige un alcance antes de hacerlo visible.");
        return;
      }
    } else {
      nextScope = "internal";
    }
    try {
      await api.patch(`/construction-reports/${r.report_id}`, {
        visibility_scope: nextScope,
      });
      await load();
    } catch (e) {
      setError(formatError(e));
    }
  };

  const remove = async (r) => {
    const ok = window.confirm(
      `¿Eliminar el reporte "${r.title}"?\n\n` +
      "También se eliminarán las fotos/archivos asociados a este reporte. " +
      "Esta acción es IRREVERSIBLE."
    );
    if (!ok) return;
    try {
      await api.delete(`/construction-reports/${r.report_id}`);
      await load();
    } catch (e) {
      setError(formatError(e));
    }
  };

  if (!investor) return null;

  return (
    <div data-testid="operaciones-tab-reportes">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-stone-600">
          {reports.length} {reports.length === 1 ? "reporte" : "reportes"} para{" "}
          <span className="font-medium">{investor.name || investor.email}</span>
        </div>
        <button
          className="dv-btn dv-btn-primary text-sm"
          onClick={() => { resetForm(); setShowForm((v) => !v); }}
          data-testid="reportes-create-toggle"
        >
          <Plus size={14} /> {showForm ? "Cancelar" : "Nuevo reporte"}
        </button>
      </div>

      {error && (
        <div className="mb-3 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800">
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="dv-card mb-4 grid sm:grid-cols-2 gap-3" data-testid="reportes-form">
          <input
            className="dv-input sm:col-span-2"
            placeholder="Título del reporte"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            data-testid="reportes-form-title"
          />
          {form.use_simple_picker ? (
            <div className="sm:col-span-2 space-y-2">
              <label className="block text-xs text-stone-500">
                Villa o alcance
                <select
                  className="dv-input w-full mt-1 text-sm"
                  value={form.picker_value}
                  onChange={(e) => onPickerChange(e.target.value)}
                  required
                  data-testid="reportes-form-villa-picker"
                >
                  <option value="">— Selecciona —</option>
                  <option value={PROJECT_WIDE_VALUE} data-testid="reportes-form-villa-project-all">
                    Todas las villas / Proyecto general
                  </option>
                  {villas.length > 0 && (
                    <optgroup label="Villas individuales">
                      {villas.map((v) => (
                        <option key={v.villa_id} value={v.villa_id}>
                          {v.name || v.villa_id}{v.model ? ` · ${v.model}` : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </label>
              {form.picker_value === PROJECT_WIDE_VALUE && (
                <div
                  className="text-[11px] px-2 py-1.5 rounded border bg-amber-50 border-amber-200 text-amber-900"
                  data-testid="reportes-form-project-all-notice"
                >
                  Este reporte será visible para <strong>todos los inversores activos</strong> como una actualización del proyecto general.
                </div>
              )}
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, use_simple_picker: false }))}
                className="text-[11px] text-stone-500 hover:text-stone-800 underline underline-offset-2"
                data-testid="reportes-form-advanced-open"
              >
                Visibilidad avanzada
              </button>
            </div>
          ) : (
            <>
              <div className="sm:col-span-2">
                <VisibilityScopeSelector
                  value={form.scope}
                  onChange={(scope) => setForm({
                    ...form,
                    scope,
                    // Keep picker_value aligned when toggling between modes.
                    picker_value: scope === "project_all"
                      ? PROJECT_WIDE_VALUE
                      : (scope === "villa" ? (form.villa_id || "") : ""),
                  })}
                  confirmProjectAll={form.confirm_project_all}
                  onConfirmProjectAll={(v) => setForm({ ...form, confirm_project_all: v })}
                  testidPrefix="reportes-form"
                />
              </div>
              {form.scope === "villa" && (
                <select
                  className="dv-input sm:col-span-2"
                  value={form.villa_id}
                  onChange={(e) => setForm({ ...form, villa_id: e.target.value, picker_value: e.target.value })}
                  data-testid="reportes-form-villa"
                  required
                >
                  <option value="">— Selecciona la villa para el alcance —</option>
                  {villas.map((v) => (
                    <option key={v.villa_id} value={v.villa_id}>{v.name || v.villa_id}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={() => setForm((f) => ({
                  ...f,
                  use_simple_picker: true,
                  picker_value: f.scope === "project_all"
                    ? PROJECT_WIDE_VALUE
                    : (f.scope === "villa" ? (f.villa_id || "") : ""),
                }))}
                className="sm:col-span-2 text-[11px] text-stone-500 hover:text-stone-800 underline underline-offset-2 text-left"
                data-testid="reportes-form-advanced-close"
              >
                ← Volver al selector simple
              </button>
            </>
          )}
          <input
            className="dv-input"
            placeholder="Periodo (ej. Abril 2026)"
            value={form.reporting_period}
            onChange={(e) => setForm({ ...form, reporting_period: e.target.value })}
          />
          <select
            className="dv-input"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            className="dv-input"
            type="number"
            min="0"
            max="100"
            placeholder="Avance %"
            value={form.progress_pct}
            onChange={(e) => setForm({ ...form, progress_pct: e.target.value })}
          />
          <input
            className="dv-input"
            placeholder="Hito completado (opcional)"
            value={form.completed_milestone}
            onChange={(e) => setForm({ ...form, completed_milestone: e.target.value })}
          />
          <input
            className="dv-input"
            placeholder="Próximo hito (opcional)"
            value={form.next_milestone}
            onChange={(e) => setForm({ ...form, next_milestone: e.target.value })}
          />
          <textarea
            className="dv-input sm:col-span-2"
            placeholder="Resumen del periodo"
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            rows={3}
          />
          <input
            className="dv-input sm:col-span-2"
            placeholder="URL del PDF (opcional)"
            value={form.pdf_url}
            onChange={(e) => setForm({ ...form, pdf_url: e.target.value })}
          />
          <textarea
            className="dv-input sm:col-span-2"
            placeholder="URLs de fotos (una por línea, opcional)"
            value={form.photo_urls_text}
            onChange={(e) => setForm({ ...form, photo_urls_text: e.target.value })}
            rows={3}
          />
          {/* Visibility is now derived from the scope selector above; the
              old "Visible para el inversor" checkbox was removed to avoid
              two conflicting controls. */}
          <div className="sm:col-span-2 flex gap-2">
            <button disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="reportes-form-submit">
              {busy ? "Guardando…" : (editId ? "Guardar cambios" : "Crear reporte")}
            </button>
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="dv-btn dv-btn-ghost">Cancelar</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="dv-card text-center py-6 text-stone-500 text-sm">Cargando…</div>
      ) : reports.length === 0 ? (
        <div className="dv-card text-center py-8 text-stone-500 text-sm">
          Aún no hay reportes de obra para este inversor.
        </div>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <div
              key={r.report_id}
              className="dv-card"
              data-testid={`reportes-row-${r.report_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.title}</span>
                    <span className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border ${STATUS_TONE[r.status] || STATUS_TONE.on_track}`}>
                      {STATUS_OPTIONS.find((o) => o.value === r.status)?.label || r.status}
                    </span>
                    {/* Visible / Oculto pill — derived from scope (internal → Oculto). */}
                    {(() => {
                      const visible = r.visibility_scope
                        ? r.visibility_scope !== "internal"
                        : !!r.visible_to_investor;
                      return visible ? (
                        <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                          <Eye size={10} /> Visible
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border bg-stone-100 text-stone-600 border-stone-200">
                          <EyeSlash size={10} /> Oculto
                        </span>
                      );
                    })()}
                    <ScopeChip scope={r.visibility_scope} />
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {r.reporting_period || "Sin periodo"} · Avance {r.progress_pct ?? 0}%
                  </div>
                  {r.summary && (
                    <div className="text-sm text-stone-700 mt-2 whitespace-pre-line">{r.summary}</div>
                  )}
                  {(r.pdf_url || (r.photo_urls && r.photo_urls.length > 0)) && (
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      {r.pdf_url && (
                        <a href={r.pdf_url} target="_blank" rel="noreferrer" className="px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100">
                          Abrir PDF
                        </a>
                      )}
                      {(r.photo_urls || []).slice(0, 6).map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noreferrer" className="px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100">
                          Foto {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => startEdit(r)}
                    className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100"
                    data-testid={`reportes-edit-${r.report_id}`}
                  >Editar</button>
                  <button
                    onClick={() => toggleVisibility(r)}
                    className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100"
                    data-testid={`reportes-toggle-visible-${r.report_id}`}
                  >
                    {(r.visibility_scope ? r.visibility_scope !== "internal" : !!r.visible_to_investor)
                      ? "Marcar interno"
                      : "Hacer visible"}
                  </button>
                  <button
                    onClick={() => remove(r)}
                    className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"
                  >
                    <Trash size={12} /> Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
